// Sync engine: submits queued offline sales ONE AT A TIME with Idempotency-Key = local UUID. A reload/retry can
// never duplicate a sale: the server replays the same invoice for a known key.
import api from "@/lib/api";
import { openDB, pendingSales, putSale, saveCatalogue } from "./db";
import { setConn, getConn } from "./connectivity";
import { beginInteraction, endInteraction } from "@/lib/diag";

const CATALOGUE_PAGE_SIZE = 1000; // bounded per-request row count (BUTTER-P1 Slice 2)
const PAGE_MAX_RETRIES = 2;

// BUTTER-P1 Slice 2: replace the old one-shot unbounded fetch with bounded, sequential, lite-projected pages
// (same /products?fields=lite endpoint as Slice 1, just paged via its existing limit/offset params — no new
// backend endpoint). Pages are accumulated in memory only; the existing (already-atomic, single-IndexedDB-
// transaction) saveCatalogue() is called exactly once, only after every page is fetched AND the accumulated,
// de-duplicated row count matches an independently-obtained expected total (from the existing /products/page
// endpoint, page_size=1, used only for its `total` field). If anything fails or the count doesn't match
// (catalogue changed under us mid-sync, or a network error), nothing is written and the previous, last-good
// catalogue already in IndexedDB is left exactly as it was — the activation is all-or-nothing.
async function fetchPageWithRetry(offset) {
  let attempt = 0;
  for (;;) {
    try {
      const { data } = await api.get("/products", { params: { fields: "lite", limit: CATALOGUE_PAGE_SIZE, offset } });
      return data;
    } catch (e) {
      if (attempt++ >= PAGE_MAX_RETRIES) throw e;
      await new Promise((r) => setTimeout(r, 300 * attempt)); // small backoff before retrying this one page
    }
  }
}

async function fetchValidatedCatalogue() {
  const { data: probe } = await api.get("/products/page", { params: { page: 1, page_size: 1 } });
  const expectedTotal = probe.total;
  const rows = []; const seen = new Set();
  for (let offset = 0; ; offset += CATALOGUE_PAGE_SIZE) {
    const page = await fetchPageWithRetry(offset);
    for (const p of page) { if (!seen.has(p.id)) { seen.add(p.id); rows.push(p); } }
    if (page.length < CATALOGUE_PAGE_SIZE) break; // last (short) page reached
    if (typeof expectedTotal === "number" && rows.length >= expectedTotal) break; // avoid one wasted trailing empty-page request
  }
  // Detects additions/removals/renames that shifted page boundaries while we were paginating (de-duplication
  // above already absorbs an exact duplicate at a boundary, which — combined with this count check — also
  // catches the "one item renamed past our scan position" case: a duplicate elsewhere reduces our unique
  // count below expectedTotal just the same as a straightforward removal would).
  if (typeof expectedTotal === "number" && rows.length !== expectedTotal) {
    throw new Error(`catalogue changed during sync (got ${rows.length} of ${expectedTotal}); keeping previous catalogue`);
  }
  return rows;
}

async function refreshCatalogueSafely(db) {
  try {
    const rows = await fetchValidatedCatalogue();
    await saveCatalogue(db, rows); // single IndexedDB transaction: clear+insert-all is atomic, never a partial write
  } catch { /* best-effort: previous catalogue (if any) is left untouched */ }
}

let running = false;
export async function syncPending(tenant, register, { onSynced } = {}) {
  if (running) return { skipped: true };
  running = true;
  const db = await openDB(tenant, register);
  const queue = await pendingSales(db);
  let synced = 0, review = 0, failed = 0;
  if (queue.length) setConn({ status: "SYNCING", pending: queue.length, message: `Syncing ${queue.length} offline sale${queue.length === 1 ? "" : "s"}…` });
  try {
    for (const sale of queue) {
      await putSale(db, { ...sale, sync_status: "syncing" });
      // Watchtower V2 Slice 2: a distinct pos_timing mark from live in-register checkout, so background offline-sync
      // timing/outcomes are never pooled with a cashier's live wait — reuses the existing diagnostic pipeline only.
      const cid = beginInteraction("checkout", "offline_sale_sync");
      try {
        const { data } = await api.post("/sales/offline", {
          local_id: sale.local_id, local_receipt_no: sale.local_receipt_no, local_created_at: sale.created_at, customer_id: sale.customer_id || null,
          items: sale.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, price: i.price, name: i.name })),
          amount_tendered: sale.amount_tendered, shift_id: sale.shift_id, device_id: sale.device_id, register_id: sale.register_id, location_id: sale.location_id,
        }, { headers: { "Idempotency-Key": sale.local_id } });
        const status = data.status === "needs_review" ? "needs_review" : "synced";
        await putSale(db, { ...sale, sync_status: status, server_invoice_id: data.invoice?.id, server_invoice_number: data.invoice?.invoice_number, synced_at: new Date().toISOString(), exceptions: data.exceptions || [] });
        status === "synced" ? synced++ : review++;
        endInteraction(cid, true);
        onSynced?.(sale, data);
      } catch (e) {
        endInteraction(cid, false);
        if (!e.response) { await putSale(db, { ...sale, sync_status: "pending" }); throw e; } // connection lost again: stop, keep pending
        const detail = e.response?.data?.detail;
        const transient = e.response.status === 409 || e.response.status >= 500;
        await putSale(db, { ...sale, sync_status: transient ? "pending" : "failed", last_error: typeof detail === "string" ? detail : detail?.message || `HTTP ${e.response.status}` });
        failed++;
      }
    }
    if (queue.length) {
      await new Promise((r) => setTimeout(r, Math.random() * 800)); // small jitter: smooths out several registers reconnecting at once
      await refreshCatalogueSafely(db);
      const left = (await pendingSales(db)).length;
      setConn({ status: failed && !synced && !review ? "SYNC_ERROR" : "ONLINE", pending: left, lastSync: new Date().toISOString(),
        message: failed ? `${failed} sale${failed === 1 ? "" : "s"} need${failed === 1 ? "s" : ""} attention. Open Offline Sync.` : review ? `All offline sales synced. ${review} flagged for review in Offline Sync.` : "All offline sales synced." });
      setTimeout(() => { if (getConn().status === "ONLINE") setConn({ message: "" }); }, 8000);
    }
  } catch { setConn({ status: "OFFLINE", pending: (await pendingSales(db)).length }); }
  finally { running = false; }
  return { synced, review, failed };
}
